import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  BarChart,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Home,
  TrendingUp,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getPocketExpenseBreakdown,
  getPocketRevenueBreakdown,
} from '../../utils/pocketListingBreakdown';
import { getBuildingImageGallery } from '../../utils/propertyImages';

function sanitizeMarketingHtml(html) {
  const raw = String(html || '').trim();
  if (!raw) return '';
  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target', 'rel'],
  });
}

function LineTable({ rows, formatMoney, t }) {
  if (!rows.length) {
    return <p className="text-sm text-slate-500">{t('pocketListing.noBreakdownLines')}</p>;
  }
  return (
    <table className="w-full text-sm border-collapse">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-b border-slate-100 last:border-0">
            <td className="py-1.5 pr-2 text-slate-600">{row.label}</td>
            <td className="py-1.5 text-right font-medium text-slate-900 tabular-nums">
              {formatMoney(row.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * En-tête « fiche immeuble » pour les partages pocket : photo, description marketing, métriques cliquables.
 */
export default function PocketDealListingHero({ property, advancedExpenses }) {
  const { t, language } = useLanguage();
  const [openGross, setOpenGross] = useState(false);
  const [openExp, setOpenExp] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const formatMoney = (value) =>
    new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatPercent = (value) =>
    value || value === 0 ? `${Number(value).toFixed(1)}%` : '—';

  const fullAddress = [property.address, property.city, property.province, property.postalCode]
    .filter(Boolean)
    .join(', ');

  const marketingHtml = useMemo(() => {
    const raw =
      language === 'en'
        ? (property.marketingDescriptionHtmlEn || property.marketingDescriptionHtml || '')
        : (property.marketingDescriptionHtml || property.marketingDescriptionHtmlEn || '');
    return sanitizeMarketingHtml(raw);
  }, [
    property.marketingDescriptionHtml,
    property.marketingDescriptionHtmlEn,
    language,
  ]);

  const imageGallery = useMemo(() => getBuildingImageGallery(property), [property]);
  const imageCount = imageGallery.length;

  const galleryKey = imageGallery.join('|');
  useEffect(() => {
    setPhotoIndex(0);
  }, [property?.id, galleryKey]);

  const revenue = useMemo(() => getPocketRevenueBreakdown(property), [property]);
  const expenses = useMemo(
    () => getPocketExpenseBreakdown(property, advancedExpenses),
    [property, advancedExpenses],
  );

  const revenueRows = useMemo(() => {
    const rows = revenue.lines.map((line) => ({
      key: line.key,
      label: t(`revenue.${line.key}`),
      amount: line.amount,
    }));
    if (revenue.vacancyAmount > 0) {
      rows.push({
        key: 'vacancy',
        label: t('pocketListing.vacancyAllowance'),
        amount: -revenue.vacancyAmount,
      });
    }
    return rows;
  }, [revenue, t]);

  const expenseRows = useMemo(() => {
    return expenses.lines.map((line) => ({
      key: line.key,
      label: t(`operatingExpenses.${line.key}`),
      amount: line.amount,
    }));
  }, [expenses.lines, t]);

  const pricePerDoor =
    property.numberOfUnits && property.purchasePrice
      ? Number(property.purchasePrice) / Number(property.numberOfUnits)
      : null;

  const topCards = [
    {
      id: 'purchase',
      label: t('building.purchasePrice'),
      value: formatMoney(Number(property.purchasePrice)),
      icon: Home,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      id: 'units',
      label: t('building.numberOfUnits'),
      value: property.numberOfUnits || '—',
      icon: Building2,
      color: 'text-slate-600',
      bg: 'bg-slate-50',
    },
    {
      id: 'ppu',
      label: t('building.pricePerDoor'),
      value: pricePerDoor ? formatMoney(pricePerDoor) : '—',
      icon: DollarSign,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
  ];

  const ratioCards = [
    {
      id: 'grm',
      label: t('building.grm'),
      value: property.grossRentMultiplier ? property.grossRentMultiplier.toFixed(1) : '—',
      icon: BarChart,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50/80',
    },
    {
      id: 'nim',
      label: t('building.nim'),
      value: property.netIncomeMultiplier ? property.netIncomeMultiplier.toFixed(1) : '—',
      icon: BarChart,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50/80',
    },
    {
      id: 'cap',
      label: t('building.capRate'),
      value: formatPercent(property.capRate),
      icon: TrendingUp,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
  ];

  const kpiCardShell =
    'rounded-xl border border-slate-200 bg-white p-4 shadow-sm';
  const kpiRowCardClass = `flex items-center gap-4 ${kpiCardShell}`;

  return (
    <div className="mb-8 space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-900 shadow-xl">
        <div className="aspect-[21/9] min-h-[200px] sm:min-h-[240px] relative">
          {imageCount > 0 ? (
            <>
              <img
                src={imageGallery[Math.min(photoIndex, imageCount - 1)]}
                alt=""
                className="h-full w-full object-cover"
              />
              {imageCount > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setPhotoIndex((i) => (i <= 0 ? imageCount - 1 : i - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/45 text-white hover:bg-black/60 shadow-lg"
                    aria-label={t('pocketListing.photoPrev')}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhotoIndex((i) => (i >= imageCount - 1 ? 0 : i + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/45 text-white hover:bg-black/60 shadow-lg"
                    aria-label={t('pocketListing.photoNext')}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                    {imageGallery.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPhotoIndex(i)}
                        className={`h-2 rounded-full transition-all ${
                          i === photoIndex ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
                        }`}
                        aria-label={`${i + 1} / ${imageCount}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
              <div className="text-center px-6">
                <Building2 className="mx-auto h-14 w-14 text-white/40" aria-hidden />
                <p className="mt-3 text-sm font-medium text-white/70">{t('pocketListing.noPhoto')}</p>
              </div>
            </div>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/80">
            {t('pocketListing.badge')}
          </p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-white drop-shadow-sm">
            {t('pocketListing.headline')}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-white/95 max-w-2xl">
            {fullAddress || t('home.address.unset')}
          </p>
        </div>
      </div>

      {marketingHtml ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">{t('pocketListing.about')}</h2>
          <div
            className="prose prose-sm max-w-none text-slate-700 prose-p:my-2 prose-ul:my-2 prose-headings:text-slate-900"
            dangerouslySetInnerHTML={{ __html: marketingHtml }}
          />
        </div>
      ) : null}

      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t('pocketListing.keyFigures')}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {topCards.map(({ id, label, value, icon: Icon, color, bg }) => (
            <div key={id} className={kpiRowCardClass}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${bg}`}>
                <Icon className={`h-5 w-5 ${color}`} aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm text-slate-600">{label}</p>
                <p className="text-lg font-semibold text-slate-900 tabular-nums">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <h3 className="text-base font-semibold text-slate-900">{t('pocketListing.revenueExpenseSectionTitle')}</h3>
          <p className="mt-1 text-sm text-slate-600">{t('pocketListing.revenueExpenseSectionHint')}</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setOpenGross((o) => !o)}
              className={`flex w-full flex-col text-left sm:h-full ${kpiCardShell} ring-1 ring-teal-200/70 border-teal-200/90 bg-teal-50/25 transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-500/35 ${
                openGross ? 'shadow-md' : 'hover:border-teal-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <DollarSign className="h-5 w-5 text-green-700" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600">{t('building.grossIncome')}</p>
                  <p className="text-lg font-semibold text-slate-900 tabular-nums">
                    {formatMoney(property.totalGrossRevenue)}
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openGross ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">{t('pocketListing.tapForSources')}</p>
              {openGross && (
                <div className="mt-3 border-t border-slate-200/80 pt-3">
                  <LineTable rows={revenueRows} formatMoney={formatMoney} t={t} />
                  <div className="mt-3 flex justify-between border-t border-slate-200 pt-2 text-sm font-semibold text-slate-900">
                    <span>{t('pocketListing.effectiveGross')}</span>
                    <span className="tabular-nums">{formatMoney(revenue.effectiveGrossRevenue)}</span>
                  </div>
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={() => setOpenExp((o) => !o)}
              className={`flex w-full flex-col text-left sm:h-full ${kpiCardShell} ring-1 ring-teal-200/70 border-teal-200/90 bg-teal-50/25 transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-500/35 ${
                openExp ? 'shadow-md' : 'hover:border-teal-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100">
                  <DollarSign className="h-5 w-5 text-red-700" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600">{t('building.totalExpenses')}</p>
                  <p className="text-lg font-semibold text-slate-900 tabular-nums">
                    {formatMoney(property.totalExpenses)}
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openExp ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">{t('pocketListing.tapForDetail')}</p>
              {openExp && (
                <div className="mt-3 border-t border-slate-200/80 pt-3">
                  <LineTable rows={expenseRows} formatMoney={formatMoney} t={t} />
                  <p className="mt-3 text-xs text-slate-500">{t('pocketListing.expenseTotalNote')}</p>
                </div>
              )}
            </button>

            <div className={`${kpiRowCardClass} sm:h-full sm:min-h-0 ring-1 ring-violet-200/60 border-violet-200/80 bg-violet-50/20`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-50">
                <TrendingUp className="h-5 w-5 text-violet-600" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm text-slate-600">{t('building.netIncome')}</p>
                <p className="text-lg font-semibold text-slate-900 tabular-nums">
                  {formatMoney(property.netOperatingIncome)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ratioCards.map(({ id, label, value, icon: Icon, color, bg }) => (
            <div key={id} className={kpiRowCardClass}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${bg}`}>
                <Icon className={`h-5 w-5 ${color}`} aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm text-slate-600">{label}</p>
                <p className="text-lg font-semibold text-slate-900 tabular-nums">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
